import React, { ChangeEvent } from 'react'
import { useDispatch } from 'react-redux'

import { Typography } from '@telavita-core/react-design-kit'
import { RadioButton, TextInput } from 'components'
import { showModal } from 'redux/actions'
import { modalTypes } from 'containers/ModalManager/ModalManager'

import * as S from '../styles'
import { COUNTRY_STATES } from 'settings/_countryStatesSettings'
import Dropdown from 'components/Dropdown'
import { MedicalRequestTypeEnum } from 'settings/_planGroupSettings'

import { FieldsData, PlanSettingsData } from '../types'

interface MedicRequestProps {
  selectedOption: boolean | undefined;
  inputPlaceholder: string;
  inputMessage: string;
  inputDisplayValue: string;
  medicRequestIsValid: boolean;
  formFields: FieldsData;
  planSettings: PlanSettingsData
  showMedicRequestOption: boolean
  handleUpdateRadioValue: (value: boolean | undefined) => void;
  handleUpdateMedicRequestFile: (event: ChangeEvent<HTMLInputElement>) => void;
  handleUpdateCrmValue: (value: string | number) => void
  handleUpdateCidValue: (value: string | number) => void
  handleUpdateUfValue: (value: string) => void
  handleUpdateAuthorizationGuideNumbers: (value: string) => void
}

export const MedicRequest = ({
  inputPlaceholder,
  inputMessage,
  inputDisplayValue,
  medicRequestIsValid,
  selectedOption,
  formFields,
  planSettings,
  showMedicRequestOption = false,
  handleUpdateRadioValue,
  handleUpdateMedicRequestFile,
  handleUpdateCrmValue,
  handleUpdateCidValue,
  handleUpdateUfValue,
  handleUpdateAuthorizationGuideNumbers
}: MedicRequestProps): JSX.Element => {
  const dispatch = useDispatch()
  const isAuthorizationGuide = planSettings?.medicalRequest?.type === MedicalRequestTypeEnum.AUTHORIZATION_GUIDE
  const shouldShowFields = isAuthorizationGuide ? true : selectedOption === true

  const handleOpenExampleModal = () => {
    dispatch(showModal(modalTypes.AUTHORIZATION_GUIDE_EXAMPLE))
  }

  return (
    <>
      {showMedicRequestOption && !isAuthorizationGuide && (
        <S.FormContainer>
          <Typography variant='content2'>Possui pedido médico?</Typography>
          <S.RadioContainer>
            <RadioButton
              iconHeight='24px'
              iconWidth='24px'
              onClickRadioButton={() => handleUpdateRadioValue(true)}
              radioId='medic_request_true'
              selected={selectedOption === true}
              title='Sim'
              data-cy='medic-request-true'
            />

            <RadioButton
              iconHeight='24px'
              iconWidth='24px'
              onClickRadioButton={() => handleUpdateRadioValue(false)}
              radioId='medic_request_false'
              selected={selectedOption === false}
              title='Não'
              data-cy='medic-request-false'
            />
          </S.RadioContainer>
        </S.FormContainer>
      )}
      {shouldShowFields && (
        <>
          <S.InputContainer>
            <TextInput
              addIcon={true}
              placeholder={inputPlaceholder}
              name='medic_request'
              hasError={!medicRequestIsValid && (inputDisplayValue !== '')}
              iconProps={{
                fill: '#898989',
                height: '20px',
                name: 'UploadFile',
                width: '20px'
              }}
              inputHeight='4.8rem'
              messageError={inputMessage}
              value={inputDisplayValue}
              readOnly
            />

            <input
              id='hiddenInput-file'
              type='file'
              onChange={e => handleUpdateMedicRequestFile(e)}
              accept='.jpg,.jpeg,.png,.bmp,.pdf'
              data-cy='medic-request-input-file'
            />
          </S.InputContainer>

          {isAuthorizationGuide && planSettings?.medicalRequest?.helperText && (
            <S.HelperText style={{ marginBottom: 10 }}>
              {planSettings.medicalRequest.helperText}
            </S.HelperText>
          )}

          {isAuthorizationGuide && planSettings?.medicalRequest?.authorizationGuideNumbers && (
            <S.InputWrapper>
              <TextInput
                id='authorization_guide_numbers'
                placeholder={planSettings.medicalRequest.authorizationGuideNumbers.placeholder}
                onInputChange={(e: React.FormEvent<HTMLInputElement>) => {
                  const numericValue = e.currentTarget.value.replace(/\D/g, '')
                  handleUpdateAuthorizationGuideNumbers(numericValue)
                }}
                value={formFields.authorizationGuideNumbers?.value || ''}
                hasError={!formFields.authorizationGuideNumbers?.isValid && (formFields.authorizationGuideNumbers?.value !== undefined && formFields.authorizationGuideNumbers?.value !== '')}
                messageError={formFields.authorizationGuideNumbers?.message}
              />
              {planSettings.medicalRequest.authorizationGuideNumbers.helperText && (
                <S.HelperTextContainer>
                  <S.HelperTextWithIcon>
                    {planSettings.medicalRequest.authorizationGuideNumbers.helperText}
                  </S.HelperTextWithIcon>
                  <S.HelpIconButton
                    type="button"
                    onClick={handleOpenExampleModal}
                    aria-label="Ver exemplo de guia de autorização"
                  >
                    <S.StyledHelpIcon />
                  </S.HelpIconButton>
                </S.HelperTextContainer>
              )}
            </S.InputWrapper>
          )}

          {shouldShowFields && (planSettings.diseaseInfo && planSettings.diseaseInfo.cid) && (
            <S.InputWrapper>
              <TextInput
                id='cid'
                maxLength={10}
                placeholder='CID (letras e números)'
                onInputChange={(e: React.FormEvent<HTMLInputElement>) => handleUpdateCidValue(e.currentTarget.value)}
                messageError={formFields.cid.message}
                hasError={!formFields.cid.isValid && formFields.cid.value !== undefined}
              />
              {planSettings && planSettings.diseaseInfo?.helperText && (
                <S.HelperText style={{ marginBottom: 10 }}>
                    *Consta no pedido médico. Digite apenas a letra e os números que aparecem depois do hífen no CID (Código Internacional de Doenças).
                </S.HelperText>
              )}
            </S.InputWrapper>
          )}
          {shouldShowFields && (planSettings.doctorInfo && planSettings.doctorInfo.crm) && (
            <S.InputWrapper>
              <TextInput
                id='crm_number'
                maxLength={10}
                placeholder='Número do CRM'
                onInputChange={(e: React.FormEvent<HTMLInputElement>) => handleUpdateCrmValue(e.currentTarget.value)}
                value={formFields.crm.value}
                hasError={!formFields.crm.isValid && formFields.crm.value !== undefined}
                messageError={formFields.crm.message}
              />
            </S.InputWrapper>
          )}
          {shouldShowFields && (planSettings.doctorInfo && planSettings.doctorInfo.uf) && (
            <S.InputWrapper>
              <Dropdown
                name='plan'
                options={COUNTRY_STATES}
                onSelect={value => handleUpdateUfValue(value)}
                placeholder='UF do CRM (2 letras)'
                dropDownOverflow
                dropDownItensToShow='3'
              />
              <S.HelperText>
                *Os dois campos acima são dados do médico que prescreveu a terapia, e devem
                constar no pedido médico.
              </S.HelperText>
            </S.InputWrapper>
          )}
        </>
      )}
    </>
  )
}
